import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import type { UserRole } from '../types';

interface NavItem {
  label: string;
  to: string;
  roles?: UserRole[];
  /** When true the item is kept in the nav config but not rendered. */
  hidden?: boolean;
}

interface NavGroup {
  heading: string;
  items: NavItem[];
}

// Role gates fixed to match the backend RBAC matrix:
//  - Reviews / Flag queues / Passport queue   → SCORE_REVIEW / FLAG_ACK
//    not allowed to item_bank_manager.
//  - Item Health (uses SCORE_REVIEW)         → not allowed to item_bank_manager.
//  - Data-Rights queue (DATA_RIGHTS_PROCESS) → admin only.
//  - Item bank / framework / users / audit   → as before.
const REVIEW_ROLES: UserRole[] = ['admin', 'lead', 'senior_assessor', 'assessor'];
const navGroups: NavGroup[] = [
  {
    heading: 'Overview',
    // Home = the candidate workbench (needs CANDIDATE_VIEW_ASSIGNED). Hidden
    // from item_bank_manager, who has no candidate access and lands on Browse
    // Items instead.
    items: [{ label: 'Home', to: '/dashboard/', roles: REVIEW_ROLES }],
  },
  {
    heading: 'Candidates',
    items: [
      // Visible to every review role. The page itself adapts: view-all
      // roles (admin/lead/senior) see all candidates; an assessor sees the
      // candidates assigned to them (GET /candidates/mine).
      { label: 'Candidates', to: '/dashboard/candidates', roles: REVIEW_ROLES },
      {
        // Assessors get a scoped Explorer (only candidates assigned to
        // them); admin/lead/senior see the full pool. Backend scopes the
        // pre-assessment query by role.
        label: 'Explorer',
        to: '/dashboard/explorer',
        roles: REVIEW_ROLES,
      },
      {
        // Visible to every review role. The endpoint scopes the
        // distributions per role: admin/lead see the whole pool,
        // senior sees own + reports, an assessor sees only their
        // assigned candidates.
        label: 'Pool Analytics',
        to: '/dashboard/pool-analytics',
        roles: REVIEW_ROLES,
      },
      { label: 'Invite New', to: '/dashboard/candidates/new', roles: REVIEW_ROLES },
    ],
  },
  {
    heading: 'Reviews',
    items: [
      { label: 'Score Queue', to: '/dashboard/reviews', roles: REVIEW_ROLES },
      { label: 'Profile Review', to: '/dashboard/profile-review', roles: REVIEW_ROLES },
      { label: 'Flag Queues', to: '/dashboard/flag-queue', roles: REVIEW_ROLES },
    ],
  },
  {
    // "Item bank" now folds in Item health (which is item-bank monitoring).
    // The old "Workflow" group has been removed; Data-rights moved to
    // Administration where it belongs alongside Audit log + Users.
    heading: 'Item Bank',
    items: [
      {
        label: 'Browse Items',
        to: '/dashboard/items',
        roles: ['admin', 'lead', 'item_bank_manager'],
      },
      {
        // Points at the question-bank uploader (single `items` sheet), whose
        // frontend/backend contract matches. The older /items/upload page
        // expected a validate/confirm protocol the backend never implemented.
        label: 'Upload Items',
        to: '/dashboard/items/upload-question-bank',
        roles: ['admin', 'lead', 'item_bank_manager'],
      },
      { label: 'Item Health', to: '/dashboard/items/health', roles: REVIEW_ROLES },
    ],
  },
  {
    heading: 'Portfolio',
    items: [
      {
        label: 'Task Documents',
        to: '/dashboard/portfolio',
        roles: ['admin', 'lead', 'item_bank_manager', 'senior_assessor'],
      },
    ],
  },
  {
    heading: 'Custom JD',
    items: [
      {
        label: 'JD Frameworks',
        to: '/dashboard/stage0',
        roles: ['admin', 'lead', 'senior_assessor'],
      },
    ],
  },
  {
    heading: 'Framework',
    items: [
      {
        label: 'View Competencies',
        to: '/dashboard/framework',
        roles: ['admin', 'lead'],
      },
      {
        label: 'Upload Competencies',
        to: '/dashboard/framework/upload',
        roles: ['admin', 'lead'],
      },
      {
        // Hidden per request (kept for easy re-enable: remove `hidden`).
        label: 'Taxonomy',
        to: '/dashboard/framework/taxonomy',
        roles: ['admin', 'lead'],
        hidden: true,
      },
    ],
  },
  {
    heading: 'Administration',
    items: [
      { label: 'Users', to: '/dashboard/users', roles: ['admin', 'lead', 'senior_assessor'] },
      { label: 'Audit Log', to: '/dashboard/audit', roles: ['admin'] },
      { label: 'Data-Rights Queue', to: '/dashboard/data-rights', roles: ['admin'] },
      { label: 'Content Review', to: '/dashboard/content-review', roles: ['lead', 'item_bank_manager'] },
    ],
  },
];

function NavItemLink({
  item,
  activeTo,
  onNavigate,
}: {
  item: NavItem;
  activeTo: string | null;
  onNavigate?: () => void;
}) {
  const isActive = item.to === activeTo;

  return (
    <NavLink
      to={item.to}
      onClick={onNavigate}
      aria-current={isActive ? 'page' : undefined}
      className={[
        // 44px-height target on desktop (py-3 = 24px line + padding)
        'group relative flex min-h-[40px] items-center pl-5 pr-3 py-2.5 font-sans text-[14px] leading-tight transition-colors duration-150',
        'focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-1 focus-visible:outline-[var(--color-primary)] rounded-[3px]',
        isActive
          ? 'font-semibold text-[var(--color-primary-ink)] bg-[var(--color-primary-soft)]'
          : 'text-[var(--color-ink-muted)] hover:text-[var(--color-ink)] hover:bg-[var(--color-surface-muted)]',
      ].join(' ')}
    >
      {/* Left indicator bar — solid blue when active, hairline preview on hover */}
      <span
        aria-hidden="true"
        className={[
          'absolute left-0 top-1/2 h-[20px] w-[3px] -translate-y-1/2 rounded-r-[2px] bg-[var(--color-primary)]',
          'origin-center transition-transform duration-200 ease-out',
          isActive ? 'scale-y-100' : 'scale-y-0 group-hover:scale-y-50',
        ].join(' ')}
      />
      {item.label}
    </NavLink>
  );
}

export function Sidebar({ onNavigate }: { onNavigate?: () => void } = {}) {
  const { user } = useAuth();
  const location = useLocation();

  const visibleGroups = navGroups
    .map((group) => ({
      ...group,
      items: group.items.filter(
        (item) =>
          !item.hidden &&
          (!item.roles || (user?.role && item.roles.includes(user.role)))
      ),
    }))
    .filter((group) => group.items.length > 0);

  // Exactly one active route: the LONGEST nav `to` the current path matches
  // (exact, or as a sub-route via `to/…`). Prevents a parent like
  // /dashboard/items from also highlighting when on /dashboard/items/upload.
  const activeTo =
    visibleGroups
      .flatMap((g) => g.items.map((i) => i.to))
      .filter((to) =>
        to === '/dashboard/'
          ? location.pathname === '/dashboard/' ||
            location.pathname === '/dashboard'
          : location.pathname === to ||
            location.pathname.startsWith(to + '/'),
      )
      .sort((a, b) => b.length - a.length)[0] ?? null;

  return (
    <nav
      aria-label="Main navigation"
      className="flex h-full flex-col gap-7 px-5 py-8"
    >
      {visibleGroups.map((group, idx) => (
        <div key={group.heading}>
          {idx > 0 && (
            <div
              aria-hidden="true"
              className="mb-7 h-px bg-[var(--color-rule)]"
            />
          )}
          <p className="eyebrow mb-3 pl-5">{group.heading}</p>
          <ul role="list" className="space-y-0.5">
            {group.items.map((item) => (
              <li key={item.to}>
                <NavItemLink
                  item={item}
                  activeTo={activeTo}
                  onNavigate={onNavigate}
                />
              </li>
            ))}
          </ul>
        </div>
      ))}

      {/* Footer block */}
      <div className="mt-auto pt-8">
        <div className="h-px w-full bg-[var(--color-rule)]" />
        <p className="mt-5 pl-5 font-mono text-[10.5px] uppercase tracking-[0.16em] text-[var(--color-ink-soft)]">
          Prism · Mediant Labs
        </p>
      </div>
    </nav>
  );
}
